import {
  CreateGHADiscountRuleBody,
  CreateGHADiscountRuleDiscountType,
  GHADiscountRule,
  GHADiscountRuleAccountType,
  GHADiscountRuleDiscountType,
} from '~/services/ConnectionGoogleService';
import { formatDateWithClock } from '~/services/TimeService';

import { GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS } from './constants';

export type GHADiscountRuleMode = 'universal' | 'country';

export type GHADiscountCountryDiscountEntry = {
  countryCode: string;
  type: CreateGHADiscountRuleDiscountType;
  value: string;
};

type CreateGHADiscountRuleCountryDiscounts = NonNullable<CreateGHADiscountRuleBody['countryDiscounts']>;

export type GHADiscountRuleFormState = {
  propertyId: string;
  accountType?: GHADiscountRuleAccountType;
  mode: GHADiscountRuleMode;
  discountType: CreateGHADiscountRuleDiscountType;
  discountValue: string;
  countryDiscounts: Array<GHADiscountCountryDiscountEntry>;
  minAdvanceBookingWindow: string;
  minLengthOfStay: string;
};

export function toCreateGHADiscountRuleBody(
  form: GHADiscountRuleFormState & { accountType: GHADiscountRuleAccountType },
): CreateGHADiscountRuleBody {
  const body: CreateGHADiscountRuleBody = {
    propertyId: form.propertyId.trim(),
    accountType: form.accountType,
    minAdvanceBookingWindow: Number(form.minAdvanceBookingWindow),
    minLengthOfStay: Number(form.minLengthOfStay),
  };

  if (form.mode === 'country') {
    body.countryDiscounts = form.countryDiscounts.reduce<CreateGHADiscountRuleCountryDiscounts>((acc, entry) => {
      if (isCountryDiscountEntryComplete(entry)) {
        acc[normalizeCountryCode(entry.countryCode)] = {
          type: entry.type,
          value: Number(entry.value),
        };
      }

      return acc;
    }, {});

    return body;
  }

  body.discountType = form.discountType;
  body.discountValue = Number(form.discountValue);

  return body;
}

export function formatDiscountAccountType(value: string) {
  return GHA_DISCOUNT_ACCOUNT_TYPE_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

export function formatDiscountType(value: GHADiscountRuleDiscountType | null) {
  switch (value) {
    case 'percentage':
      return 'Percentage';
    case 'margin_retention':
      return 'Margin retention';
    case null:
      return 'Country-specific';
    default:
      return value;
  }
}

function formatSingleDiscount({
  type,
  value,
}: {
  type: CreateGHADiscountRuleDiscountType | GHADiscountRuleDiscountType;
  value: number | null;
}) {
  if (!type || value === null) {
    return 'Country-specific';
  }

  if (type === 'margin_retention') {
    return `Retain ${value}% margin`;
  }
  return `${value}% off`;
}

export function formatCountryDiscounts(countryDiscounts?: GHADiscountRule['countryDiscounts'] | null) {
  if (!countryDiscounts || Object.keys(countryDiscounts).length === 0) {
    return 'Country-specific';
  }

  return Object.entries(countryDiscounts)
    .sort(([countryCodeA], [countryCodeB]) => countryCodeA.localeCompare(countryCodeB))
    .map(([countryCode, discount]) => `${countryCode}: ${formatSingleDiscount(discount)}`)
    .join(', ');
}

export function formatRuleDiscount(rule: GHADiscountRule) {
  if (rule.countryDiscounts && Object.keys(rule.countryDiscounts).length > 0) {
    return formatCountryDiscounts(rule.countryDiscounts);
  }

  return formatSingleDiscount({ type: rule.discountType, value: rule.discountValue });
}

function normalizeCountryCode(value: string) {
  return value.trim().toUpperCase();
}

export function isValidCountryCode(value: string) {
  return /^[A-Z]{2}$/.test(value);
}

export function getDuplicateCountryCodes(entries: Array<GHADiscountCountryDiscountEntry>) {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  entries.forEach((entry) => {
    const countryCode = normalizeCountryCode(entry.countryCode);

    if (!countryCode) {
      return;
    }

    if (seen.has(countryCode)) {
      duplicates.add(countryCode);
      return;
    }

    seen.add(countryCode);
  });

  return Array.from(duplicates).sort();
}

export function isCountryDiscountEntryComplete(entry: GHADiscountCountryDiscountEntry) {
  return Boolean(entry.countryCode.trim() && entry.value !== '');
}

export function formatDateTime(value?: string | null) {
  return value ? formatDateWithClock(value) : '-';
}

// ---------------------------------------------------------------------------
// GHA-429 effective-discount matrix (supplier rows x viewer-country columns)
// ---------------------------------------------------------------------------

export function formatEffectiveDiscount(
  discountType: 'percentage' | 'margin_retention' | null,
  discountValue: number | null,
): string {
  if (discountType == null || discountValue == null) {
    return '—';
  }
  return formatSingleDiscount({ type: discountType, value: discountValue });
}

export interface GHAEffectiveDiscountMatrixRow<TLeg> {
  supplier: string | null;
  supplierName: string | null;
  legByCountry: Record<string, TLeg>;
}

export interface GHAEffectiveDiscountMatrix<TLeg> {
  countries: Array<string>;
  rows: Array<GHAEffectiveDiscountMatrixRow<TLeg>>;
}

/** Pivots the API's flat leg list: rows in first-seen supplier order, columns in first-seen
 *  country order (the API serves landing countries in a stable order). */
export function buildEffectiveDiscountMatrix<
  TLeg extends { supplier: string | null; supplierName: string | null; userCountry: string },
>(legs: Array<TLeg>): GHAEffectiveDiscountMatrix<TLeg> {
  const countries: Array<string> = [];
  const rowsBySupplier = new Map<string, GHAEffectiveDiscountMatrixRow<TLeg>>();
  for (const leg of legs) {
    if (!countries.includes(leg.userCountry)) {
      countries.push(leg.userCountry);
    }
    const key = leg.supplier ?? '';
    let row = rowsBySupplier.get(key);
    if (!row) {
      row = { supplier: leg.supplier, supplierName: leg.supplierName, legByCountry: {} };
      rowsBySupplier.set(key, row);
    }
    row.legByCountry[leg.userCountry] = leg;
  }
  return { countries, rows: Array.from(rowsBySupplier.values()) };
}
